/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

// Web-only twin of Phoenix ProductTileView (same tokens, sizes and text
// styles) whose icon is a plain <img>, so the tile is complete in the server
// HTML. Phoenix's Icon (expo-image) only paints after hydration.

import { CSSProperties, FC } from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { pd } from '@airasia-rn/ui';
import { ProductTileViewType } from '@airasia-phoenix/react-native/components/ProductTileView';
import {
  getColor,
  getShape,
  getTextStyle,
} from '@airasia-phoenix/react-native/utils/utils';
import type { ProductTileWebProps } from './types';

export type { ProductTileWebProps } from './types';

const ProductTileWeb: FC<ProductTileWebProps> = ({
  type = ProductTileViewType.REGULAR,
  iconUrl,
  tint,
  primaryText,
  primaryTextStyle,
  secondaryText,
  onPress,
  testID,
}) => {
  const isSmall = type === ProductTileViewType.SMALL;
  const tokens = isSmall
    ? pd.comp.product_tile_small
    : pd.comp.product_tile_regular;
  const size = isSmall ? 48 : 70;
  const iconStyle = {
    ...getShape(tokens.container.shape),
    width: size,
    height: size,
    display: 'block',
    objectFit: 'contain',
  } as CSSProperties;

  return (
    <Pressable
      accessibilityRole="button"
      testID={testID}
      onPress={onPress}
      style={styles.tile}
    >
      <View style={isSmall ? styles.iconContainerSmall : styles.iconContainer}>
        {tint ? (
          <div
            style={{
              ...iconStyle,
              backgroundColor: tint,
              WebkitMaskImage: `url(${iconUrl})`,
              maskImage: `url(${iconUrl})`,
              WebkitMaskSize: 'contain',
              maskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskPosition: 'center',
            }}
          />
        ) : (
          <img
            src={iconUrl}
            alt=""
            width={size}
            height={size}
            style={iconStyle}
          />
        )}
      </View>
      <Text
        numberOfLines={2}
        style={[
          getTextStyle(
            primaryTextStyle ?? tokens.primaryText.type,
            tokens.primaryText.colour,
          ),
          isSmall ? styles.textSmall : styles.text,
        ]}
      >
        {primaryText}
      </Text>
      {secondaryText ? (
        <Text
          numberOfLines={2}
          style={[
            getTextStyle(
              tokens.secondaryText.type,
              tokens.secondaryText.colour,
            ),
            isSmall ? styles.textSmall : styles.text,
          ]}
        >
          {secondaryText}
        </Text>
      ) : null}
    </Pressable>
  );
};

export default ProductTileWeb;

const styles = StyleSheet.create((pd) => ({
  tile: {
    alignItems: 'center',
  },
  iconContainer: {
    ...getShape(pd.comp.product_tile_regular.container.shape),
    backgroundColor: getColor(pd.comp.product_tile_regular.container.colour),
    marginBottom: pd.ref.space.spacing10.px,
    width: 70,
    height: 70,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainerSmall: {
    ...getShape(pd.comp.product_tile_small.container.shape),
    backgroundColor: getColor(pd.comp.product_tile_small.container.colour),
    marginBottom: pd.ref.space.spacing10.px,
    width: 48,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Phoenix caps the text at 76 (regular) / 70 (small), which also sets the
  // tile's width in a row.
  text: {
    textAlign: 'center',
    maxWidth: 76,
  },
  textSmall: {
    textAlign: 'center',
    maxWidth: 70,
  },
}));
